import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ArrowDown, ArrowUp, ChevronDown, ChevronsUpDown, Download, RefreshCw } from 'lucide-react';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import { GoHoverTooltip } from './GoHoverTooltip';

const POP_WIDTH = 340;
const POP_GAP = 6;
const VIEWPORT_PAD = 8;

export const GO_DEV_GROUP_BY_OPTIONS = [
  { key: 'status', label: 'Status' },
  { key: 'appVersion', label: 'Versão App' },
  { key: 'none', label: 'Nenhum' },
];

/** Botão inteiriço: Exibição (ordenação, agrupamento, colunas, exportar) | Atualizar. */
export function GoDevicesDisplayMenu({
  sortItems,
  sortBy,
  sortDir,
  onSortChange,
  onSortDirChange,
  groupBy,
  onGroupByChange,
  colItems,
  isColVis,
  onToggleCol,
  onRestoreCols,
  colsCustomized,
  onExport,
  exportDisabled,
  exportCount,
  onRefresh,
  refreshDisabled,
  refreshBusy,
}) {
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.min(POP_WIDTH, window.innerWidth - VIEWPORT_PAD * 2);
    const left = Math.min(
      Math.max(VIEWPORT_PAD, r.right - width),
      window.innerWidth - width - VIEWPORT_PAD
    );
    const top = r.bottom + POP_GAP;
    setPos({ top, left, width, maxHeight: Math.max(220, window.innerHeight - top - 12) });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const sortLabel = sortItems.find((i) => i.value === sortBy)?.label ?? '';

  return (
    <div className="go-dev-split">
      <button
        ref={btnRef}
        type="button"
        className={`go-dev-split-btn go-dev-split-btn--main${open ? ' is-open' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <IconAdjustmentsHorizontal size={15} stroke={1.75} aria-hidden />
        <span className="go-dev-split-label">Exibição</span>
        {sortLabel ? (
          <span className="go-dev-split-meta">
            · {sortLabel}{sortDir ? ` ${sortDir === 'asc' ? '↑' : '↓'}` : ''}
          </span>
        ) : null}
        <ChevronDown size={13} strokeWidth={2.25} className="go-dev-split-chev" aria-hidden />
      </button>
      {onRefresh ? (
        <>
          <span className="go-dev-split-sep" aria-hidden />
          <GoHoverTooltip text="Atualizar lista" align="end">
            <button
              type="button"
              className="go-dev-split-btn"
              onClick={onRefresh}
              disabled={refreshDisabled}
              aria-label="Atualizar"
            >
              <RefreshCw
                size={14}
                strokeWidth={1.75}
                aria-hidden
                className={refreshBusy ? 'go-dev-refresh-spin' : undefined}
              />
              <span>Atualizar</span>
            </button>
          </GoHoverTooltip>
        </>
      ) : null}

      {open && pos
        ? ReactDOM.createPortal(
            <div
              ref={popRef}
              className="go-dev-display-pop"
              role="dialog"
              aria-label="Opções de exibição"
              style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
            >
              <div className="go-dev-display-sec">
                <div className="go-dev-display-sec-title">Ordenar por</div>
                <div className="go-dev-display-sort-row">
                  <div className="go-dev-display-select-wrap">
                    <select
                      className="go-dev-display-select"
                      value={sortBy}
                      onChange={(e) => onSortChange(e.target.value)}
                      aria-label="Ordenar por"
                    >
                      {sortItems.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <ChevronsUpDown size={14} strokeWidth={2} className="go-dev-display-select-icon" aria-hidden />
                  </div>
                  {onSortDirChange ? <div className="go-dev-display-seg" role="group" aria-label="Direção">
                    <button
                      type="button"
                      className={sortDir === 'asc' ? 'is-active' : ''}
                      aria-pressed={sortDir === 'asc'}
                      onClick={() => onSortDirChange('asc')}
                    >
                      <ArrowUp size={13} strokeWidth={2.25} aria-hidden />
                      Asc
                    </button>
                    <button
                      type="button"
                      className={sortDir === 'desc' ? 'is-active' : ''}
                      aria-pressed={sortDir === 'desc'}
                      onClick={() => onSortDirChange('desc')}
                    >
                      <ArrowDown size={13} strokeWidth={2.25} aria-hidden />
                      Desc
                    </button>
                  </div> : null}
                </div>
              </div>

              {onGroupByChange ? <div className="go-dev-display-sec">
                <div className="go-dev-display-sec-title">Agrupar por</div>
                <div className="go-dev-display-group-opts">
                  {GO_DEV_GROUP_BY_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      className={`go-dev-display-group-opt${groupBy === opt.key ? ' is-active' : ''}`}
                      aria-pressed={groupBy === opt.key}
                      onClick={() => onGroupByChange(opt.key)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div> : null}

              <div className="go-dev-display-sec">
                <div className="go-dev-display-sec-head">
                  <span className="go-dev-display-sec-title">Colunas visíveis</span>
                  <button
                    type="button"
                    className="go-dev-display-link"
                    onClick={onRestoreCols}
                    disabled={!colsCustomized}
                  >
                    Restaurar padrão
                  </button>
                </div>
                <div className="go-dev-display-cols">
                  {colItems.map((item) => {
                    const on = isColVis(item.value);
                    return (
                      <button
                        key={item.value}
                        type="button"
                        className="go-dev-display-col"
                        role="switch"
                        aria-checked={on}
                        onClick={() => onToggleCol(item.value)}
                      >
                        <span className="go-dev-display-col-label">{item.label}</span>
                        <span className={`go-dev-display-switch${on ? ' is-on' : ''}`} aria-hidden>
                          <span className="go-dev-display-switch-knob" />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {onExport ? <div className="go-dev-display-foot">
                <button
                  type="button"
                  className="go-dev-display-export"
                  onClick={() => {
                    onExport();
                    setOpen(false);
                  }}
                  disabled={exportDisabled}
                >
                  <Download size={14} strokeWidth={1.9} aria-hidden />
                  <span>Exportar XLSX</span>
                  <span className="go-dev-display-export-hint">
                    {Number(exportCount || 0).toLocaleString('pt-BR')} linha{exportCount === 1 ? '' : 's'}
                  </span>
                </button>
              </div> : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
